import type { WeldDrillDownRow } from '../components/dashboard/drilldown/drillDownTypes';
import { rowKey } from './weldDrillDownModel';

export const WPS_PARAM_DEFS = [
  { key: 'current', label: 'Current', viewKey: 'current', limitMin: 'current_min', limitMax: 'current_max' },
  { key: 'volts', label: 'Voltage', viewKey: 'volt', limitMin: 'volts_min', limitMax: 'volts_max' },
  { key: 'travel_speed', label: 'Travel speed', viewKey: 'travelSpeed', limitMin: 'travel_speed_min', limitMax: 'travel_speed_max' },
  { key: 'wire_speed', label: 'Wire feed speed', viewKey: 'wireSpeed', limitMin: 'wire_speed_min', limitMax: 'wire_speed_max' },
  { key: 'heat', label: 'Heat input', viewKey: 'heat', limitMin: 'heat_min', limitMax: 'heat_max' },
  { key: 'true_energy', label: 'True energy', viewKey: 'trueEnergy', limitMin: 'true_energy_min', limitMax: 'true_energy_max' },
  { key: 'oscillation_width', label: 'Oscillation width', viewKey: 'oscillationWidth', limitMin: 'oscillation_width_min', limitMax: 'oscillation_width_max' },
] as const;

export type WpsParamKey = (typeof WPS_PARAM_DEFS)[number]['key'];

/** Display-only sentinel for out-of-limit welds with no per-parameter breaches. */
export const OVERALL_WPS_PARAMETER_KEY = 'overall_wps' as const;
export const OVERALL_WPS_PARAMETER_LABEL = 'Overall WPS';
export const OVERALL_WPS_HINT =
  'Overall WPS status failed; no individual parameter breach was recorded. Review pass/zone/tilt status for the project check method.';

export interface ParamValueTriple {
  min: number | null;
  avg: number | null;
  max: number | null;
  avgStatus?: boolean | string | null;
  lowStatus?: boolean | string | null;
  highStatus?: boolean | string | null;
}

export interface LimitsRow {
  job_number?: string;
  pass_name?: string;
  zone_name?: string;
  [key: string]: unknown;
}

export interface WpsViolationRecord {
  parameterKey: WpsParamKey | 'overall_wps';
  parameterLabel: string;
  actual: number | null;
  min: number | null;
  max: number | null;
  deviationAbs: number | null;
  deviationPct: number | null;
  direction: 'above' | 'below' | 'unknown';
  level: 'pass' | 'zone' | 'tilt' | 'weld';
  passName?: string;
  zoneName?: string;
  tiltStart?: number | null;
  tiltEnd?: number | null;
  welderId?: string;
}

export interface ProductionImpactSummary {
  failedWeldCount: number;
  affectedPassCount: number;
  uniqueParameters: string[];
  uniqueWelders: string[];
}

export interface SimilarWeldMatch {
  weldNumber: string;
  jobNumber: string;
  matchingParameters: string[];
  score: number;
}

export function isStatusFailed(status: unknown): boolean {
  if (status === false || status === 0) return true;
  const s = String(status ?? '').toUpperCase();
  return (
    s === 'FALSE' ||
    s === '0' ||
    s === 'FAIL' ||
    s === 'FAILED' ||
    s === 'CROSS' ||
    s === 'NULL' ||
    s === 'REJECTED'
  );
}

export function isStatusPassed(status: unknown): boolean {
  if (status === true || status === 1) return true;
  const s = String(status ?? '').toUpperCase();
  return s === 'TRUE' || s === '1' || s === 'PASS' || s === 'PASSED';
}

export function computeDeviation(
  actual: number,
  min: number | null,
  max: number | null,
): Pick<WpsViolationRecord, 'deviationAbs' | 'deviationPct' | 'direction'> {
  if (!Number.isFinite(actual)) {
    return { deviationAbs: null, deviationPct: null, direction: 'unknown' };
  }
  if (min == null && max == null) {
    return { deviationAbs: null, deviationPct: null, direction: 'unknown' };
  }
  if (max != null && actual > max) {
    const deviationAbs = actual - max;
    const deviationPct = max !== 0 ? (deviationAbs / Math.abs(max)) * 100 : null;
    return { deviationAbs, deviationPct, direction: 'above' };
  }
  if (min != null && actual < min) {
    const deviationAbs = min - actual;
    const deviationPct = min !== 0 ? (deviationAbs / Math.abs(min)) * 100 : null;
    return { deviationAbs, deviationPct, direction: 'below' };
  }
  return { deviationAbs: 0, deviationPct: 0, direction: 'unknown' };
}

export function getCorrectiveHint(
  parameterKey: WpsParamKey | 'overall_wps',
  direction: WpsViolationRecord['direction'],
): string {
  if (parameterKey === 'overall_wps') {
    return OVERALL_WPS_HINT;
  }
  const above = direction === 'above';
  const hints: Record<WpsParamKey, { above: string; below: string }> = {
    current: {
      above: 'Check wire feed, stick-out, and machine calibration — current may be too high.',
      below: 'Check wire feed speed and contact tip condition — current may be too low.',
    },
    volts: {
      above: 'Check voltage setpoint and wire feed — arc length may be too long.',
      below: 'Check voltage setpoint and gas coverage — arc may be too short.',
    },
    travel_speed: {
      above: 'Travel speed is above WPS limit — reduce speed or verify encoder calibration.',
      below: 'Travel speed is below WPS limit — increase speed; slow travel raises heat input.',
    },
    wire_speed: {
      above: 'Wire feed speed is high — verify WFS setting and drive rolls.',
      below: 'Wire feed speed is low — check liner, drive rolls, and spool tension.',
    },
    heat: {
      above: 'Heat input high — reduce voltage/current or increase travel speed.',
      below: 'Heat input low — increase energy or reduce travel speed.',
    },
    true_energy: {
      above: 'True energy above limit — review travel speed and arc energy settings.',
      below: 'True energy below limit — review travel speed and arc energy settings.',
    },
    oscillation_width: {
      above: 'Oscillation width above limit — check weave settings on the bug.',
      below: 'Oscillation width below limit — check weave settings on the bug.',
    },
  };
  const entry = hints[parameterKey];
  return above ? entry.above : entry.below;
}

function readLimit(limitsRow: LimitsRow | null | undefined, minKey: string, maxKey: string) {
  if (!limitsRow) return { min: null, max: null };
  const minRaw = limitsRow[minKey];
  const maxRaw = limitsRow[maxKey];
  const min = minRaw != null && minRaw !== '' ? Number(minRaw) : null;
  const max = maxRaw != null && maxRaw !== '' ? Number(maxRaw) : null;
  return {
    min: min != null && Number.isFinite(min) ? min : null,
    max: max != null && Number.isFinite(max) ? max : null,
  };
}

function paramFailed(param: ParamValueTriple | null | undefined): boolean {
  if (!param) return false;
  return (
    isStatusFailed(param.avgStatus) ||
    isStatusFailed(param.lowStatus) ||
    isStatusFailed(param.highStatus)
  );
}

function pickActual(param: ParamValueTriple | null | undefined, direction: WpsViolationRecord['direction']): number | null {
  if (!param) return null;
  if (direction === 'above' && param.max != null) return param.max;
  if (direction === 'below' && param.min != null) return param.min;
  return param.avg ?? param.max ?? param.min ?? null;
}

function inferDirection(param: ParamValueTriple, min: number | null, max: number | null): WpsViolationRecord['direction'] {
  const avg = param.avg;
  if (avg != null && max != null && avg > max) return 'above';
  if (avg != null && min != null && avg < min) return 'below';
  if (isStatusFailed(param.highStatus)) return 'above';
  if (isStatusFailed(param.lowStatus)) return 'below';
  return 'unknown';
}

function normalizeLimitsKey(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

export function findLimitsRow(
  limits: LimitsRow[],
  jobNumber: string,
  passName?: string,
  zoneName?: string,
): LimitsRow | null {
  if (!limits.length) return null;

  const normalizedJob = normalizeLimitsKey(jobNumber);
  const normalizedPass = passName?.trim() ? normalizeLimitsKey(passName) : '';
  const normalizedZone = zoneName?.trim() ? normalizeLimitsKey(zoneName) : '';

  const matchesJob = (row: LimitsRow) => normalizeLimitsKey(row.job_number) === normalizedJob;
  const matchesPass = (row: LimitsRow) =>
    !normalizedPass || normalizeLimitsKey(row.pass_name) === normalizedPass;
  const matchesZone = (row: LimitsRow) =>
    !normalizedZone || normalizeLimitsKey(row.zone_name) === normalizedZone;

  const exact = limits.find((row) => matchesJob(row) && matchesPass(row) && matchesZone(row));
  if (exact) return exact;

  if (normalizedPass) {
    const jobPass = limits.find((row) => matchesJob(row) && matchesPass(row));
    if (jobPass) return jobPass;
  }

  const jobOnly = limits.find(matchesJob);
  if (jobOnly) return jobOnly;

  const projectDefault = limits.find((row) => !normalizeLimitsKey(row.job_number));
  return projectDefault ?? null;
}

/** True when a numeric reading sits outside configured WPS min/max. */
function valueOutsideLimits(
  actual: number | null | undefined,
  min: number | null,
  max: number | null,
): boolean {
  if (actual == null || !Number.isFinite(actual)) return false;
  if (min == null && max == null) return false;
  if (max != null && actual > max) return true;
  if (min != null && actual < min) return true;
  return false;
}

function pickValueBasedActual(
  param: ParamValueTriple,
  min: number | null,
  max: number | null,
): { actual: number; direction: WpsViolationRecord['direction'] } | null {
  // Prefer avg; also catch low/high readings that alone sit outside the band.
  const candidates: { value: number | null | undefined; prefer: WpsViolationRecord['direction'] }[] = [
    { value: param.avg, prefer: 'unknown' },
    { value: param.min, prefer: 'below' },
    { value: param.max, prefer: 'above' },
  ];
  for (const { value, prefer } of candidates) {
    if (!valueOutsideLimits(value, min, max)) continue;
    const actual = value as number;
    const fromCompute = computeDeviation(actual, min, max);
    const direction =
      fromCompute.direction !== 'unknown'
        ? fromCompute.direction
        : prefer !== 'unknown'
          ? prefer
          : inferDirection(param, min, max);
    return { actual, direction };
  }
  return null;
}

export function violationsFromViewRecord(
  record: {
    params: Partial<Record<string, ParamValueTriple | null>>;
    jobNumber?: string;
    passName?: string;
    zoneName?: string;
    tiltStart?: number | null;
    tiltEnd?: number | null;
    welderId?: string;
  },
  limits: LimitsRow[],
  level: WpsViolationRecord['level'],
): WpsViolationRecord[] {
  const limitsRow = findLimitsRow(
    limits,
    record.jobNumber ?? '',
    record.passName,
    record.zoneName,
  );
  const violations: WpsViolationRecord[] = [];

  WPS_PARAM_DEFS.forEach((def) => {
    const param = record.params[def.viewKey];
    if (!param) return;

    const { min, max } = readLimit(limitsRow, def.limitMin, def.limitMax);
    const statusFailed = paramFailed(param);

    let direction: WpsViolationRecord['direction'];
    let actual: number | null;
    let deviation: Pick<WpsViolationRecord, 'deviationAbs' | 'deviationPct' | 'direction'>;

    if (statusFailed) {
      direction = inferDirection(param, min, max);
      actual = pickActual(param, direction);
      deviation =
        actual != null && Number.isFinite(actual)
          ? computeDeviation(actual, min, max)
          : { deviationAbs: null, deviationPct: null, direction };
    } else {
      // Empty status-flag path: compare actuals to WPS limits when configured.
      if (min == null && max == null) return;
      const valueBased = pickValueBasedActual(param, min, max);
      if (!valueBased) return;
      direction = valueBased.direction;
      actual = valueBased.actual;
      deviation = computeDeviation(actual, min, max);
    }

    violations.push({
      parameterKey: def.key,
      parameterLabel: def.label,
      actual: actual != null && Number.isFinite(actual) ? actual : null,
      min,
      max,
      ...deviation,
      direction: deviation.direction !== 'unknown' ? deviation.direction : direction,
      level,
      passName: record.passName,
      zoneName: record.zoneName,
      tiltStart: record.tiltStart,
      tiltEnd: record.tiltEnd,
      welderId: record.welderId,
    });
  });

  return violations;
}

export function aggregateFailuresByParameter(violations: WpsViolationRecord[]): { parameter: string; count: number }[] {
  const counts: Record<string, number> = {};
  violations.filter(Boolean).forEach((v) => {
    counts[v.parameterLabel] = (counts[v.parameterLabel] ?? 0) + 1;
  });
  return Object.entries(counts)
    .map(([parameter, count]) => ({ parameter, count }))
    .sort((a, b) => b.count - a.count);
}

export function worstDeviationPct(violations: WpsViolationRecord[]): number | null {
  const pcts = violations
    .map((v) => v.deviationPct)
    .filter((p): p is number => p != null && Number.isFinite(p));
  if (!pcts.length) return null;
  return Math.max(...pcts);
}

export function summarizeViolationsForRow(violations: WpsViolationRecord[]): {
  failedParameters: string[];
  worstDeviationPct: number | null;
  hints: string[];
} {
  const failedParameters = Array.from(new Set(violations.map((v) => v.parameterLabel)));
  const hints = violations.map((v) => getCorrectiveHint(v.parameterKey, v.direction));
  return {
    failedParameters,
    worstDeviationPct: worstDeviationPct(violations),
    hints: Array.from(new Set(hints)),
  };
}

export function aggregateCorrectiveHints(
  violations: WpsViolationRecord[],
  limit = 8,
): { hint: string; count: number }[] {
  const counts = new Map<string, number>();
  violations.forEach((v) => {
    const hint = getCorrectiveHint(v.parameterKey, v.direction);
    counts.set(hint, (counts.get(hint) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([hint, count]) => ({ hint, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

const ACTUAL_DECIMALS: Partial<Record<WpsParamKey, number>> = {
  travel_speed: 1,
};

export function formatViolationActual(
  actual: number | null | undefined,
  parameterKey?: WpsParamKey | 'overall_wps',
): string {
  if (actual == null || !Number.isFinite(actual)) return '—';
  if (!parameterKey || parameterKey === 'overall_wps') {
    return actual.toFixed(2);
  }
  const decimals = ACTUAL_DECIMALS[parameterKey] ?? 2;
  return actual.toFixed(decimals);
}

function formatLimitNumber(n: number): string {
  return Number(n).toFixed(2);
}

export function formatWpsLimitRange(min: number | null, max: number | null): string {
  if (min != null && max != null) {
    return `${formatLimitNumber(min)} – ${formatLimitNumber(max)}`;
  }
  if (min != null) return `≥ ${formatLimitNumber(min)}`;
  if (max != null) return `≤ ${formatLimitNumber(max)}`;
  return '—';
}

export interface ViolationContext {
  level: 'pass' | 'zone' | 'tilt' | 'weld';
  passName?: string;
  zoneName?: string;
  tiltStart?: number | null;
  tiltEnd?: number | null;
}

export interface GroupedViolation {
  parameterKey: WpsParamKey | 'overall_wps';
  parameterLabel: string;
  direction: WpsViolationRecord['direction'];
  actual: number | null;
  actualMin: number | null;
  actualMax: number | null;
  min: number | null;
  max: number | null;
  worstDeviationPct: number | null;
  contexts: ViolationContext[];
  hint: string;
  locationCount: number;
}

function contextKey(ctx: ViolationContext): string {
  return [
    ctx.level,
    ctx.passName ?? '',
    ctx.zoneName ?? '',
    ctx.tiltStart ?? '',
    ctx.tiltEnd ?? '',
  ].join('::');
}

export function formatViolationContextLabel(ctx: ViolationContext): string {
  if (ctx.level === 'weld') {
    return 'Entire weld';
  }
  if (ctx.level === 'tilt' && ctx.tiltStart != null && ctx.tiltEnd != null) {
    return `Tilt ${ctx.tiltStart}–${ctx.tiltEnd}°`;
  }
  if (ctx.zoneName) {
    return ctx.passName ? `Pass ${ctx.passName} · Zone ${ctx.zoneName}` : `Zone ${ctx.zoneName}`;
  }
  if (ctx.passName) return `Pass ${ctx.passName}`;
  return ctx.level;
}

export function wpsDirectionLabel(direction: WpsViolationRecord['direction']): string {
  if (direction === 'below') return 'Below WPS minimum';
  if (direction === 'above') return 'Above WPS maximum';
  return 'Out of WPS limit';
}

export function formatContextList(contexts: ViolationContext[], cap = 6): string {
  if (!contexts.length) return '';
  const labels = contexts.map(formatViolationContextLabel);
  if (labels.length <= cap) return labels.join(' · ');
  const visible = labels.slice(0, cap).join(' · ');
  return `${visible} · +${labels.length - cap} more`;
}

export function groupViolationsByParameter(violations: WpsViolationRecord[]): GroupedViolation[] {
  const buckets = new Map<string, WpsViolationRecord[]>();

  violations.forEach((v) => {
    const key = `${v.parameterKey}::${v.direction}`;
    const list = buckets.get(key) ?? [];
    list.push(v);
    buckets.set(key, list);
  });

  const groups: GroupedViolation[] = [];

  buckets.forEach((records) => {
    const worst = records.reduce((best, v) => {
      const bestPct = best.deviationPct ?? -1;
      const vPct = v.deviationPct ?? -1;
      return vPct > bestPct ? v : best;
    }, records[0]);

    const actuals = records
      .map((v) => v.actual)
      .filter((a): a is number => a != null && Number.isFinite(a));
    const contextMap = new Map<string, ViolationContext>();

    records.forEach((v) => {
      const ctx: ViolationContext = {
        level: v.level,
        passName: v.passName,
        zoneName: v.zoneName,
        tiltStart: v.tiltStart,
        tiltEnd: v.tiltEnd,
      };
      contextMap.set(contextKey(ctx), ctx);
    });

    groups.push({
      parameterKey: worst.parameterKey,
      parameterLabel: worst.parameterLabel,
      direction: worst.direction,
      actual: worst.actual,
      actualMin: actuals.length ? Math.min(...actuals) : null,
      actualMax: actuals.length ? Math.max(...actuals) : null,
      min: worst.min,
      max: worst.max,
      worstDeviationPct: worst.deviationPct,
      contexts: Array.from(contextMap.values()),
      hint: getCorrectiveHint(worst.parameterKey, worst.direction),
      locationCount: records.length,
    });
  });

  return groups.sort((a, b) => (b.worstDeviationPct ?? 0) - (a.worstDeviationPct ?? 0));
}

export function computeProductionImpact(
  failedRows: WeldDrillDownRow[],
  violationsByRowKey: Record<string, WpsViolationRecord[]>,
): ProductionImpactSummary {
  const uniqueParameters = new Set<string>();
  const uniqueWelders = new Set<string>();
  let affectedPassCount = 0;

  failedRows.forEach((row) => {
    const violations = violationsByRowKey[rowKey(row)] ?? [];
    affectedPassCount += violations.filter((v) => v.level === 'pass').length;
    violations.forEach((v) => {
      uniqueParameters.add(v.parameterLabel);
      if (v.welderId && v.welderId !== '—') uniqueWelders.add(v.welderId);
    });
  });

  return {
    failedWeldCount: failedRows.length,
    affectedPassCount,
    uniqueParameters: Array.from(uniqueParameters),
    uniqueWelders: Array.from(uniqueWelders),
  };
}

export function findSimilarWelds(
  rows: WeldDrillDownRow[],
  target: WeldDrillDownRow,
  violationsByRowKey: Record<string, WpsViolationRecord[]>,
  limit = 5,
): SimilarWeldMatch[] {
  const targetKey = rowKey(target);
  const targetParams = new Set(
    (violationsByRowKey[targetKey] ?? []).map((v) => v.parameterKey),
  );
  if (!targetParams.size) return [];

  const matches: SimilarWeldMatch[] = [];
  rows.forEach((row) => {
    if (rowKey(row) === targetKey) return;
    if (row.outcome !== 'fail') return;
    const params = new Set((violationsByRowKey[rowKey(row)] ?? []).map((v) => v.parameterKey));
    const intersection = Array.from(targetParams).filter((p) => params.has(p));
    if (!intersection.length) return;
    const score = intersection.length / targetParams.size;
    matches.push({
      weldNumber: row.weldNumber,
      jobNumber: row.jobNumber,
      matchingParameters: intersection,
      score,
    });
  });

  return matches.sort((a, b) => b.score - a.score).slice(0, limit);
}

export function compactViolationReason(v: WpsViolationRecord): string {
  if (v.parameterKey === OVERALL_WPS_PARAMETER_KEY) {
    return OVERALL_WPS_PARAMETER_LABEL;
  }
  if (v.direction === 'below') return `${v.parameterLabel} below min`;
  if (v.direction === 'above') return `${v.parameterLabel} above max`;
  return v.parameterLabel;
}

/** Pass-name match for attributing zone/tilt breaches back onto a pass summary row. */
export function violationsForPassName(
  violations: WpsViolationRecord[],
  passName: string,
): WpsViolationRecord[] {
  const normalized = normalizeLimitsKey(passName);
  if (!normalized) return [];
  return violations.filter((v) => normalizeLimitsKey(v.passName) === normalized);
}

export function limitsConfiguredForJob(
  limits: LimitsRow[],
  jobNumber: string,
): boolean {
  return findLimitsRow(limits, jobNumber) != null;
}

export function violationsLackLimits(violations: WpsViolationRecord[] | null | undefined): boolean {
  if (!violations?.length) return false;
  return violations.some((v) => v != null && v.min == null && v.max == null);
}

export function formatDeviation(v: WpsViolationRecord): string {
  if (v.min == null && v.max == null) return '—';
  if (v.deviationPct != null) {
    const sign = v.direction === 'below' ? '−' : '+';
    return `${sign}${Math.abs(v.deviationPct).toFixed(1)}%`;
  }
  return '—';
}

/** Display-only: out-of-limit weld with no per-parameter breach records. */
export function buildOverallWpsLocationRecords(): WpsViolationRecord[] {
  return [
    {
      parameterKey: OVERALL_WPS_PARAMETER_KEY,
      parameterLabel: OVERALL_WPS_PARAMETER_LABEL,
      actual: null,
      min: null,
      max: null,
      deviationAbs: null,
      deviationPct: null,
      direction: 'unknown',
      level: 'weld',
      welderId: '—',
    },
  ];
}

export function buildOverallWpsGroupedViolation(): GroupedViolation {
  return {
    parameterKey: OVERALL_WPS_PARAMETER_KEY,
    parameterLabel: OVERALL_WPS_PARAMETER_LABEL,
    direction: 'unknown',
    actual: null,
    actualMin: null,
    actualMax: null,
    min: null,
    max: null,
    worstDeviationPct: null,
    contexts: [{ level: 'weld' }],
    hint: OVERALL_WPS_HINT,
    locationCount: 1,
  };
}

export function exportDrillDownCsv(
  rows: WeldDrillDownRow[],
  violationsByRowKey: Record<string, WpsViolationRecord[]>,
  welderByRowKey: Record<string, string>,
): string {
  const headers = [
    'Weld Number',
    'Job Number',
    'WPS Number',
    'Outcome',
    'Failed Parameters',
    'Worst Deviation %',
    'Welder ID(s)',
    'Start Time',
  ];
  const lines = [headers.join(',')];

  rows.forEach((row) => {
    const violations = violationsByRowKey[rowKey(row)] ?? [];
    const summary = summarizeViolationsForRow(violations);
    const welder = welderByRowKey[rowKey(row)] ?? '—';
    const start = row.startTimeMs != null ? new Date(row.startTimeMs).toISOString() : '';
    const fields = [
      row.weldNumber,
      row.jobNumber,
      row.wpsNumber,
      row.outcome,
      summary.failedParameters.join('; '),
      summary.worstDeviationPct != null ? summary.worstDeviationPct.toFixed(1) : '',
      welder,
      start,
    ].map((f) => `"${String(f).replace(/"/g, '""')}"`);
    lines.push(fields.join(','));
  });

  return lines.join('\n');
}

export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function filterDrillDownRows(
  rows: WeldDrillDownRow[],
  filters: {
    welderIds?: string[];
    wpsNumbers?: string[];
    dateFromMs?: number | null;
    dateToMs?: number | null;
  },
  welderByRowKey: Record<string, string>,
): WeldDrillDownRow[] {
  return rows.filter((row) => {
    if (filters.wpsNumbers?.length) {
      const wps = row.wpsNumber?.trim();
      if (!wps || !filters.wpsNumbers.includes(wps)) return false;
    }
    if (filters.welderIds?.length) {
      const welder = welderByRowKey[rowKey(row)] ?? '';
      const ids = welder.split(',').map((s) => s.trim()).filter(Boolean);
      if (!ids.some((id) => filters.welderIds!.includes(id))) return false;
    }
    if (filters.dateFromMs != null && row.startTimeMs != null && row.startTimeMs < filters.dateFromMs) {
      return false;
    }
    if (filters.dateToMs != null && row.startTimeMs != null && row.startTimeMs > filters.dateToMs) {
      return false;
    }
    return true;
  });
}
